import type { Patient } from '~/domain/patient/patient';
import type { ConnectorPatientManifest } from '~/domain/persistence/ConnectorPatientManifest';

/** One of the patient's datasets that the connector has and the session does not. */
export type UnloadedDataset = {
    datasetId: string;
    /** 'Image3D' | 'StructureSet' | 'Dose' | 'Registration', for the label. */
    kind: 'Image3D' | 'StructureSet' | 'Dose' | 'Registration';
    /** The row's display label, or null when the connector recorded none. */
    seriesDescription: string | null;
};

/**
 * The patient's datasets that were not retrieved.
 *
 * The manifest describes the whole patient — it always has, since it is built
 * from the patient detail rather than from the selection — so it already knows
 * what an open left behind. Matching is by the same UIDs hydration uses: a
 * series for images, a SOP instance for everything else, since two structure
 * sets can legally share one Series UID.
 *
 * Returns them in manifest order, which is detail order, which is the order the
 * worklist showed them in.
 */
export function unloadedDatasets(
    manifest: ConnectorPatientManifest | undefined,
    patient: Patient | undefined
): UnloadedDataset[] {
    if (manifest === undefined) {
        return [];
    }

    const loadedImageSeries = new Set(
        (patient?.images ?? []).map((image) => image.seriesUid)
    );
    const loadedSopUids = new Set(
        [
            ...(patient?.structureSets ?? []),
            ...(patient?.doses ?? []),
            ...(patient?.registrations ?? []),
        ].map((dataset) => dataset.dicomUid)
    );

    const bySopUid = <
        T extends {
            datasetId: string;
            sopInstanceUid: string | null;
            seriesDescription: string | null;
        },
    >(
        entries: readonly T[],
        kind: UnloadedDataset['kind']
    ): UnloadedDataset[] =>
        entries
            // No SOP UID means nothing to match it against; treating that as
            // "not loaded" would offer a load that cannot be told apart from
            // what is already there.
            .filter(
                (entry) =>
                    entry.sopInstanceUid !== null &&
                    !loadedSopUids.has(entry.sopInstanceUid)
            )
            .map((entry) => ({
                datasetId: entry.datasetId,
                kind,
                seriesDescription: entry.seriesDescription,
            }));

    return [
        ...manifest.images
            .filter((entry) => !loadedImageSeries.has(entry.seriesInstanceUid))
            .map((entry) => ({
                datasetId: entry.datasetId,
                kind: 'Image3D' as const,
                seriesDescription: entry.seriesDescription,
            })),
        ...bySopUid(manifest.structureSets, 'StructureSet'),
        ...bySopUid(manifest.doses, 'Dose'),
        ...bySopUid(manifest.registrations, 'Registration'),
    ];
}
